/**
 * Wistia video analytics (#291, #293).
 *
 * Pushes the same dataLayer event as the GTM "cHTML - Vimeo - Listener" tag,
 * so the existing "Custom - Video - Interactions" trigger and GA4 video tag
 * send Wistia plays as video_ready, video_play, video_pause, video_progress,
 * and video_complete:
 *
 *   { event: 'video', video_action, video_provider: 'wistia', video_url,
 *     video_percent, video_title }
 *
 * Matches the Vimeo listener so GA4 and Looker reports continue unchanged:
 * - ready fires once per player when its API is ready, before any play.
 *   Wistia doesn't start a hidden player, so a modal video sends ready when
 *   the modal opens; Vimeo sends it on page load
 * - play and pause report the playback position as a whole percent
 * - progress fires at 25, 50, 75, and 100 percent of the playback position,
 *   once each, and only for the highest mark crossed (a seek to 80% sends 75)
 * - titles are lowercased, with "no title" when Wistia has none
 *
 * complete is new: the Vimeo listener never sent it.
 *
 * Wistia calls to action and forms add three actions, with their own
 * parameters (#293):
 * - cta_click: video_cta_url, video_cta_text
 * - form_view and form_submit: video_form_id, video_form_position
 *
 * A form view and submission also push the same `form` event as the Marketo
 * forms listener, with form_provider 'wistia', so GTM's form events, lead key
 * event (generate_lead), and ad conversions handle them like any other lead.
 * The form's email and names never reach the dataLayer.
 *
 * Wistia pauses the player itself when a call to action or form appears.
 * Those pauses aren't the viewer's, so they're skipped. GatedContent.js marks
 * the pause it makes when the modal closes, which is still reported.
 *
 * Every push sets all of its event's parameters, even when empty. GTM merges
 * pushes into one data model, so an omitted key would keep the value from the
 * previous event.
 *
 * Listeners sit on the document in the capture phase. Player events don't
 * bubble, and <wistia-player> drops listeners added to the element itself
 * when it's moved, which GatedContent.js does when it portals the modal to
 * <body>. Capturing also covers players added after page load. The form's
 * form-load event doesn't leave the player's shadow root, so that one is
 * captured on the shadow root instead.
 */

const PLAYER = 'wistia-player';
const PROGRESS_STEP = 0.25;
const WISTIA_HOST = /(^|\.)(wistia\.(com|net)|wi\.st)$/;

const EMPTY_EXTRAS = {
  video_cta_url: '',
  video_cta_text: '',
  video_form_id: '',
  video_form_position: '',
};

// Per-player state, keyed by element so a moved player keeps its progress.
const players = new WeakMap();
const boundShadowRoots = new WeakSet();

function push(params) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'video', video_provider: 'wistia', ...EMPTY_EXTRAS, ...params });
}

// GTM's "dlv - Form ID" reads mkto.form.values.formid for every form provider.
function pushForm(status, formId) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'form',
    form_provider: 'wistia',
    form_status: status,
    'mkto.form.values': { formid: formId },
  });
}

function positionOf(player) {
  const duration = Number(player.duration) || 0;
  if (duration <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, (Number(player.currentTime) || 0) / duration));
}

function percentOf(player) {
  return Math.round(positionOf(player) * 100);
}

function stateOf(player) {
  if (!players.has(player)) {
    const mediaId = player.getAttribute('media-id') || '';
    players.set(player, {
      url: player.dataset.videoUrl || `https://softwareag.wistia.com/medias/${mediaId}`,
      readySent: false,
      lastProgress: 0,
      // Until form-load names the form, fall back to the media ID so a lead still has an ID.
      formId: `wistia-${mediaId}`,
      formsViewed: new Set(),
    });
  }
  return players.get(player);
}

function send(player, action, percent, extras = {}) {
  const name = typeof player.name === 'string' ? player.name.trim() : '';
  push({
    video_action: action,
    video_url: stateOf(player).url,
    video_percent: percent,
    video_title: name ? name.toLowerCase() : 'no title',
    ...extras,
  });
}

function sendReady(player) {
  const state = stateOf(player);
  if (state.readySent) {
    return;
  }
  state.readySent = true;
  send(player, 'ready', 0);
}

// Where a form shows when it loads; the conversion event reports it directly.
function formPositionAt(player) {
  if (player.ended) {
    return 'post-roll';
  }
  return positionOf(player) === 0 ? 'pre-roll' : 'mid-roll';
}

function bindShadowRoot(player) {
  const root = player.shadowRoot;
  if (!root || boundShadowRoots.has(root)) {
    return;
  }
  boundShadowRoots.add(root);

  root.addEventListener(
    'form-load',
    event => {
      const state = stateOf(player);
      const formId = String(event.detail?.formHashedId || '') || state.formId;
      state.formId = formId;
      if (state.formsViewed.has(formId)) {
        return;
      }
      state.formsViewed.add(formId);
      send(player, 'form_view', percentOf(player), {
        video_form_id: formId,
        video_form_position: formPositionAt(player),
      });
      pushForm('visible', formId);
    },
    true
  );
}

const handlers = {
  'api-ready': sendReady,

  play(player) {
    sendReady(player);
    send(player, 'play', percentOf(player));
  },

  pause(player, event) {
    // A pause at the end is reported as complete, not as a pause.
    if (player.ended) {
      return;
    }
    if (player.dataset.videoPauseReason) {
      delete player.dataset.videoPauseReason;
    } else if (event.detail?.lastPauseInfo?.source === 'non-user-event') {
      // Wistia paused for a call to action or form.
      return;
    }
    send(player, 'pause', percentOf(player));
  },

  'time-update'(player) {
    const state = stateOf(player);
    const mark = Math.floor(positionOf(player) / PROGRESS_STEP) * PROGRESS_STEP;
    if (mark > state.lastProgress) {
      state.lastProgress = mark;
      send(player, 'progress', mark * 100);
    }
  },

  ended(player) {
    const state = stateOf(player);
    if (state.lastProgress < 1) {
      state.lastProgress = 1;
      send(player, 'progress', 100);
    }
    send(player, 'complete', 100);
  },

  // detail also carries the email and names; only the position is read.
  conversion(player, event) {
    const state = stateOf(player);
    const position = String(event.detail?.type || '').replace(/-email$/, '') || formPositionAt(player);
    send(player, 'form_submit', percentOf(player), {
      video_form_id: state.formId,
      video_form_position: position,
    });
    pushForm('success', state.formId);
  },
};

// Calls to action are links inside the player's shadow root. Clicks are
// composed, so the document sees them with the player as the target.
function onClick(event) {
  const path = event.composedPath();
  const playerIndex = path.findIndex(el => el.localName === PLAYER);
  if (playerIndex < 0) {
    return;
  }
  const link = path.slice(0, playerIndex).find(el => el.localName === 'a' && el.href);
  if (!link) {
    return;
  }
  let host = '';
  try {
    host = new URL(link.href).hostname;
  } catch (error) {
    return;
  }
  // The Wistia logo links to Wistia; it isn't a call to action.
  if (WISTIA_HOST.test(host)) {
    return;
  }
  const player = path[playerIndex];
  send(player, 'cta_click', percentOf(player), {
    video_cta_url: link.href,
    video_cta_text: (link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100),
  });
}

export default function VideoTracking() {
  Object.entries(handlers).forEach(([type, handler]) => {
    document.addEventListener(
      type,
      event => {
        const player = event.target;
        if (player instanceof Element && player.localName === PLAYER) {
          bindShadowRoot(player);
          handler(player, event);
        }
      },
      true
    );
  });
  document.addEventListener('click', onClick, true);

  // player.js loads async, so api-ready can fire before the listeners above
  // exist. whenApiReady() resolves either way once the element is defined.
  const existing = document.querySelectorAll(PLAYER);
  if (existing.length) {
    window.customElements.whenDefined(PLAYER).then(() => {
      existing.forEach(player => {
        player
          .whenApiReady()
          .then(() => {
            bindShadowRoot(player);
            sendReady(player);
          })
          .catch(() => {});
      });
    });
  }
}
